/**
 * A folha de impressão da equipe marcada, que o navegador salva como PDF.
 *
 * Papel: desenha, na `FolhaDeImpressao`, o título "RH Doméstico — Equipe
 * Ativa", a linha com o instante da exportação e a contagem, e a caixa
 * "Colaboradores selecionados" com a tabela de nome, cargo, CPF, residência,
 * admissão, salário base e situação.
 *
 * Como usar: as regras de montar, imprimir e desmontar são as da
 * `FolhaDeImpressao`: quem a monta só o faz no clique de "🖨 Exportar PDF", com
 * uma `key` nova a cada clique, a desmonta em `onConcluida` e esconde o resto
 * da página da impressão enquanto ela está montada.
 *
 * Sete colunas com o CPF e o salário por extenso nem sempre cabem na largura
 * de uma folha em retrato. Nome, cargo e residência quebram em mais de uma
 * linha antes de qualquer coluna faltar, e o número, a data e o CPF nunca
 * quebram; se a tabela ainda assim passar da largura do papel, a folha inteira
 * cresce junto com ela, e o navegador reduz a página para caber.
 */
import { type Colaborador } from '@concierge/shared';

import { formatarCpf, formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { contagemDeSelecionados } from '../equipe-selecionada';
import { ROTULO_DO_STATUS } from '../rotulos';
import { CaixaDaFolha, FolhaDeImpressao } from './folha-de-impressao';
import {
  CLASSES_DO_CORPO_DA_FOLHA,
  CLASSES_DO_NUMERO_DA_FOLHA,
  CLASSES_DO_TH_DA_FOLHA,
  linhaDeExportacao,
} from './partes-da-folha';

/** O cabeçalho das colunas da tabela da folha, na ordem em que saem. */
const COLUNAS_DA_FOLHA = [
  'Nome',
  'Cargo',
  'CPF',
  'Residência',
  'Admissão',
  'Salário base',
  'Status',
] as const;

interface FolhaDaEquipeProps {
  /** As pessoas marcadas, já em `ordenarPorNome`. */
  colaboradores: readonly Colaborador[];
  /** O instante do clique em "🖨 Exportar PDF", em ISO 8601 com fuso. */
  exportadaEm: string;
  /** Chamado quando a janela de impressão fecha, impressa ou cancelada. */
  onConcluida: () => void;
}

/**
 * A folha de impressão da equipe, que abre a janela de impressão ao montar.
 *
 * @param colaboradores - As pessoas marcadas, na ordem em que saem no papel.
 * @param exportadaEm - O instante da exportação, que o subtítulo escreve como
 *   `DD/MM/AAAA às HH:MM`, no horário de Brasília.
 * @param onConcluida - Chamado uma vez, quando o navegador avisa que a janela
 *   de impressão fechou (`afterprint`).
 * @returns A folha, escondida na tela e visível só na impressão.
 */
export function FolhaDaEquipe({
  colaboradores,
  exportadaEm,
  onConcluida,
}: FolhaDaEquipeProps) {
  return (
    <FolhaDeImpressao
      titulo="RH Doméstico — Equipe Ativa"
      subtitulo={`${linhaDeExportacao(exportadaEm)} · ${contagemDeSelecionados(colaboradores.length)}`}
      onConcluida={onConcluida}
    >
      <CaixaDaFolha titulo="Colaboradores selecionados">
        <table aria-label="Colaboradores selecionados" className="w-full border-collapse">
          <thead>
            <tr>
              {COLUNAS_DA_FOLHA.map((rotulo) => (
                <th
                  key={rotulo}
                  className={cn(
                    CLASSES_DO_TH_DA_FOLHA,
                    rotulo === 'Salário base' && 'text-right',
                  )}
                >
                  {rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={CLASSES_DO_CORPO_DA_FOLHA}>
            {colaboradores.map((colaborador) => (
              <tr key={colaborador.id}>
                <td className="font-semibold text-foreground">{colaborador.nomeCompleto}</td>
                <td>{colaborador.cargo}</td>
                <td className="whitespace-nowrap">{formatarCpf(colaborador.cpf)}</td>
                <td>{colaborador.residencia.apelido}</td>
                <td className="whitespace-nowrap">{formatarData(colaborador.admissao)}</td>
                <td className={CLASSES_DO_NUMERO_DA_FOLHA}>
                  {formatarDinheiro(colaborador.salario)}
                </td>
                <td className="whitespace-nowrap">{ROTULO_DO_STATUS[colaborador.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CaixaDaFolha>
    </FolhaDeImpressao>
  );
}
